/**
 * Agents hub — the **Skills** tab (`/agents/skills`): the agency's custom
 * Agent Skills catalog (upload / download / remove) with a "used by" toggle
 * per scheduled agent type. Skills are AGENCY-level — one catalog for every
 * brand — so there is no brand line here; a new upload is used by every agent
 * until it is switched off for one.
 *
 * Management surface: `portal.settings.manage`. The scheduled agent types for
 * the toggles come from the registry (`available_types`, read through the
 * active brand — the only way the API exposes it).
 */
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { useCan } from '../../../lib/rbac';
import type { RootState } from '../../../store/store';
import {
  listAgents,
  type TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import AgentsTabs from './AgentsTabs';
import CustomSkillsManager from './CustomSkillsManager';
import '../../../styles/pages/agents/agentsHub.scss';

const AgentSkillsTab = () => {
  const { t } = useTranslation();
  const canManage = useCan('portal.settings.manage');
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const [types, setTypes] = useState<TypeView[]>([]);

  const loadTypes = useCallback(async (slug: string) => {
    try {
      const index = await listAgents(slug);
      setTypes(index.available_types.filter((type) => type.kind !== 'chat'));
    } catch {
      // The toggles simply don't render without the registry; the catalog
      // itself still loads and reports its own errors.
      setTypes([]);
    }
  }, []);

  useEffect(() => {
    if (canManage && activeShopSlug) loadTypes(activeShopSlug);
  }, [canManage, activeShopSlug, loadTypes]);

  return (
    <SubPageInnerContentHolder
      title={t('Agents', 'Agents')}
      backgroundColor={'var(--main-background-color)'}
    >
      <div className='agentsHub'>
        <AgentsTabs active='skills' showSkills={canManage} />
        <p className='agentsHub__lead'>
          {t(
            'AgentSkillsLeadBefore',
            "Skills are your agency's know-how for the agents — a house reporting style, a checklist, a glossary. They apply to"
          )}{' '}
          <strong>{t('AgentSkillsLeadEveryBrand', 'every brand')}</strong>
          {t(
            'AgentSkillsLeadAfter',
            ', and to every agent unless you switch them off per agent below.'
          )}
        </p>

        {!canManage ? (
          <div className='agentsHub__empty'>
            {t(
              'AgentSkillsNoPermission',
              'Skills are managed by owners and managers. Ask for the "manage portal settings" capability to upload or change them.'
            )}
          </div>
        ) : !activeShopSlug ? (
          <div className='agentsHub__empty'>
            {t(
              'AgentsNoActiveBrand',
              'No active brand. Pick one from the button in the bottom-left.'
            )}
          </div>
        ) : (
          <CustomSkillsManager
            slug={activeShopSlug}
            canManage={canManage}
            agentTypes={types}
          />
        )}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default AgentSkillsTab;
